import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useDeleteSSHKey, useGetSSHKeys } from '@/services/sshkeys'
import { CreateSSHKey } from './CreateSSHKey'
import { CopyButton } from '@/components/copy-button'
import { Trash2, Key, RefreshCw, Terminal, Info } from 'lucide-react'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { ErrorMessage } from '@/components/error-message'
import { LoadingMessage } from '@/components/loading-message'
import dayjs from 'dayjs'

export const DashboardSSHKeys = () => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean | string>(
    false
  )
  const [activeTab, setActiveTab] = useState('manage')

  const {
    data: sshKeys = [],
    isLoading: isKeysLoading,
    error: keysError,
  } = useGetSSHKeys()

  const { mutate: deleteSSHKey, isPending: isDeletingSSHKey } =
    useDeleteSSHKey()

  const handleDeleteSSHKey = async () => {
    if (typeof openDeleteDialog !== 'string') return
    deleteSSHKey(openDeleteDialog, {
      onSuccess: () => {
        setOpenDeleteDialog(false)
      },
    })
  }

  const keysRemaining = 3 - (sshKeys?.length || 0)

  return (
    <div className="py-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl" data-testid="ssh-keys-title">
            SSH Keys
          </h1>
          <p className="text-foreground/60 mt-1">
            Manage secure access to your compute resources
          </p>
        </div>
        <Badge
          variant="outline"
          className="px-3 py-1.5 flex items-center gap-1.5"
          data-testid="keys-remaining-badge"
        >
          <Key className="h-3.5 w-3.5" />
          {keysRemaining + ' of 3 keys remaining'}
        </Badge>
      </div>

      <Tabs
        defaultValue="manage"
        value={activeTab}
        onValueChange={setActiveTab}
        className="mb-12"
        data-testid="ssh-keys-tabs"
      >
        <TabsList className="mb-6">
          <TabsTrigger
            value="manage"
            className="flex items-center gap-2"
            data-testid="manage-keys-tab"
          >
            <Key className="h-4 w-4" />
            Manage Keys
          </TabsTrigger>
          <TabsTrigger
            value="help"
            className="flex items-center gap-2"
            data-testid="help-tab"
          >
            <Info className="h-4 w-4" />
            Help & Tips
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manage" data-testid="manage-keys-content">
          {isKeysLoading ? (
            <div className="flex justify-center py-12">
              <LoadingMessage message="Loading SSH keys..." />
            </div>
          ) : (
            <div className="space-y-6" data-testid="ssh-keys-list">
              {sshKeys.length > 0 ? (
                sshKeys
                  .sort(
                    (a, b) =>
                      dayjs(a.updated_at).unix() - dayjs(b.updated_at).unix()
                  )
                  .map((key) => (
                    <Card
                      key={key.key_id}
                      className="overflow-hidden ssh-key-card"
                      data-testid={`ssh-key-card-${key.key_id}`}
                    >
                      <CardHeader className="pb-2 bg-muted/30">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <CardTitle className="text-md font-medium flex items-center gap-2">
                            <Terminal className="h-4 w-4" />
                            SSH Key
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <CopyButton
                              textToCopy={key.public_key}
                              variant="ghost"
                              size="sm"
                              data-testid={`copy-key-button-${key.key_id}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive/90 hover:bg-destructive/10 flex items-center gap-1.5"
                              onClick={() =>
                                setOpenDeleteDialog(String(key.key_id))
                              }
                              data-testid={`remove-key-button-${key.key_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">Remove</span>
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-3">
                        <div
                          className="font-mono text-xs bg-primary/5 p-3 rounded"
                          data-testid={`ssh-key-content-${key.key_id}`}
                        >
                          <div className="whitespace-pre-wrap break-all">
                            {key.public_key}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
              ) : (
                <Card
                  className="bg-muted/20 border-dashed"
                  data-testid="no-ssh-keys-card"
                >
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <Key className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-xl font-medium mb-2">
                      No SSH Keys Added
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md">
                      Add an SSH key to securely access compute resources via
                      terminal
                    </p>
                    <Button
                      onClick={() => setActiveTab('help')}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Info className="h-4 w-4" />
                      Learn about SSH Keys
                    </Button>
                  </CardContent>
                </Card>
              )}

              {keysRemaining > 0 && (
                <div className="mt-6" data-testid="create-ssh-key-section">
                  <CreateSSHKey />
                </div>
              )}
            </div>
          )}

          {keysError?.message && (
            <ErrorMessage message={keysError.message} className="mt-6" />
          )}
        </TabsContent>

        <TabsContent value="help" data-testid="help-content">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>What are SSH Keys?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  SSH keys provide a secure way to access remote servers without
                  passwords. They consist of two parts:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Public key</strong> - What you add here (safe to
                    share)
                  </li>
                  <li>
                    <strong>Private key</strong> - Kept secure on your computer
                    (never share)
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How to Generate an SSH Key</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <CodeSnippet
                  text='ssh-keygen -t ed25519 -C "your_email@example.com"'
                  prefix="$ "
                />
                <p>
                  This creates a new SSH key using the ed25519 algorithm. Your
                  public key will be in:
                </p>
                <CodeSnippet text="~/.ssh/id_ed25519.pub" />
                <Button
                  variant="outline"
                  className="w-full mt-2 flex items-center justify-center gap-2"
                  onClick={() => setActiveTab('manage')}
                >
                  <RefreshCw className="h-4 w-4" />
                  Return to Key Management
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmationDialog
        open={!!openDeleteDialog}
        onOpenChange={(open) => {
          if (!isDeletingSSHKey) {
            setOpenDeleteDialog(open ? openDeleteDialog : false)
          }
        }}
        title="Remove SSH Key"
        description="Are you sure you want to delete this SSH key? Any servers using this key may become inaccessible. This action cannot be undone."
        buttons={{
          confirm: {
            label: isDeletingSSHKey ? 'Removing...' : 'Remove Key',
            isDestructive: true,
            isLoading: isDeletingSSHKey,
          },
          cancel: {
            label: 'Cancel',
          },
        }}
        onConfirm={handleDeleteSSHKey}
        data-testid="delete-ssh-key-dialog"
      />
    </div>
  )
}

type CodeSnippetProps = {
  text: string
  prefix?: string
}

const CodeSnippet = ({ text, prefix = '' }: CodeSnippetProps) => {
  return (
    <div className="relative font-mono bg-muted/10 border border-muted/30 p-3 rounded text-sm group flex items-start justify-between">
      <span className="text-primary/90">
        {prefix}
        {text}
      </span>
      <div className="opacity-30 group-hover:opacity-100 transition-opacity ml-4">
        <CopyButton textToCopy={text} variant="ghost" size="sm" />
      </div>
    </div>
  )
}
